/**
 * ManagerListContainer
 * --
 */
import React, { useEffect, useState } from 'react';
import ManagerListPresenter from './ManagerListPresenter';
import { useGetAdmins, useUpdateAdmin, useDeleteAdmin } from 'hooks/AdminHooks';
import { useGetShopInformations } from 'hooks/ShopInformationHooks';
import { useDuplicateCheck } from 'hooks/AuthHooks';
import MessageAlert from 'utils';
import validateCheck from 'utils/Validation';
import { LoadingPage } from 'components';
import { useUserStack } from 'store';

const ManagerListContainer = (props) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  const { defaultShop = null } = props;

  /* ===== Mutate ===== */
  // 중복검사
  const { duplicate } = useDuplicateCheck();

  const userInfo = useUserStack();
  const { updateAdmin } = useUpdateAdmin();
  const { deleteAdmin } = useDeleteAdmin();
  /* ===== Data ===== */
  const query = {
    ...(defaultShop ? { shop_information_id: defaultShop } : {}),
  };
  // 관리자 전체 조회
  const { data: Admins, refetch } = useGetAdmins(query);
  const { data: ShopInformations } = useGetShopInformations(query);

  /* ===== STATE ===== */
  const [data, setData] = useState([]);
  const [shopData, setShopdata] = useState([]);
  // 모달 open 여부
  const [isOpened, setIsOpened] = useState(false);
  // 모달 타입
  const [modalType, setModalType] = useState('');
  // 메뉴 파일
  // const [files, setFiles] = useState([]);

  const [modalData, setModalData] = React.useState({
    admin: {},
  });
  const [isEditingAccount, setIsEditingAccount] = useState(false); // 계정 정보 수정 토글
  // 로딩
  const [isLoading, setIsLoading] = React.useState(false);

  // 아이디 중복검사 여부
  // NOTE: DEFAULT: 중복검사 미실행 / FAIL: 중복됨 / SUCCESS: 중복안됨
  const [isChecked, setIsChecked] = React.useState('DEFAULT');
  /* ===== VARIABLE ===== */
  /* ===== FUNCTION ===== */
  /**
   * 모달 핸들러
   * --
   */
  const handleModal = (open, type = '', data = {}) => {
    setModalData((prev) => ({
      ...prev,
      [type]: data,
    }));
    setIsOpened(!open);
    setModalType(type);
  };

  /**
   * 데이터 바인딩 함수
   * --
   * @param {string} target
   * @param {*} value
   * @param {object} object
   * @param {*} callback
   */
  const handleChange = (t, v, o, callback = null) => {
    let newData = modalData[o];

    newData[t] = v;
    const d = {
      ...modalData,
      o: { ...newData },
    };

    setModalData(d);
  };

  /**
   * 관리자 삭제
   * --
   */
  const handleDeleteSelected = async (idx) => {
    if (!idx) {
      MessageAlert.warning('삭제할 항목을 선택해주세요.');
      return;
    }
    try {
      setIsLoading(true);

      const { status } = await deleteAdmin(idx);

      if (status === 200) {
        MessageAlert.success('선택한 관리자가 삭제되었습니다.');
        refetch();
      } else {
        MessageAlert.error(
          '관리자와 적용된 다른 데이터를 확인하고 다시 시도해주세요.'
        );
      }
    } catch (err) {
      MessageAlert.error(
        '관리자와 적용된 다른 데이터를 확인하고 다시 시도해주세요.'
      );
      console.error('[DELETE ADMIN ERROR]:', err);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * admin create 함수
   * --
   */
  const handleSubmit = async (t, s = 'UPDATE') => {
    const type = t.toLocaleUpperCase();
    const submit = s.toLocaleUpperCase();

    const submitType = {
      ADMIN: '관리자',
      UPDATE: '수정',
    };

    const data = handleValidateCheck(type);
    if (!data) return;

    const submitFunc = {
      ADMIN: {
        UPDATE: updateAdmin,
      },
    };
    try {
      setIsLoading(true);

      let allSuccess = true;

      const { status } = await submitFunc[type][submit]({
        ...data,
      });

      if (status !== 200) {
        allSuccess = false;
      }

      if (allSuccess) {
        MessageAlert.success(
          `성공적으로 ${submitType[type]}이/가 ${submitType[submit]}되었습니다.`
        );
        setModalData((prev) => ({ ...prev, t: {} }));
        setIsOpened(!isOpened);
        refetch();
      } else {
        MessageAlert.warning(
          `${submitType[type]}을/를 ${submitType[submit]}하는데 실패하였습니다.`
        );
      }
    } catch (err) {
      MessageAlert.error(
        `${submitType[type]}을/를 ${submitType[submit]}하는데 오류가 발생하였습니다.`
      );
      console.error(`[CREATE ${type}] ERROR : `, err);
      throw err;
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    }
  };

  /**
   * 유효성 검사
   * --
   * @param {string} t
   * @returns
   */
  const handleValidateCheck = (t) => {
    if (t === 'ADMIN') {
      // 직원 유효성 검사
      const {
        type, // 관리자 타입
        admin_level, // 관리자 등급
        shop_information_id, // 매장/쇼핑몰
        admin_privilege, // 권한 등급
        admin_name, // 관리자 명
        admin_email, // 이메일 앞부분
        admin_phone, // 휴대폰 번호
        admin_account,
        password,
      } = modalData?.admin;
      // console.log('modalData', modalData);
      // 필수 입력값 검사
      if (!type) {
        MessageAlert.warning('관리자 타입을 선택해주세요.');
        return false;
      }
      if (!admin_level) {
        MessageAlert.warning('관리자 등급을 선택해주세요.');
        return false;
      }
      if (type !== 'ADMIN' && !shop_information_id) {
        MessageAlert.warning('매장/쇼핑몰을 선택해주세요.');
        return false;
      }
      if (!admin_privilege || admin_privilege === 'XXXX') {
        MessageAlert.warning('올바른 권한 등급을 설정해주세요.');
        return false;
      }
      if (!admin_name) {
        MessageAlert.warning('관리자 이름을 입력해주세요.');
        return false;
      }
      if (!validateCheck('email', admin_email)) {
        MessageAlert.warning('이메일 주소를 정확히 입력해주세요.');
        return false;
      }
      if (!admin_phone || admin_phone.length < 9) {
        MessageAlert.warning('휴대폰 번호를 올바르게 입력해주세요.');
        return false;
      }

      if (isEditingAccount) {
        // 관리자 아이디 중복검사 여부 체크
        if (isChecked === 'DEFAULT') {
          MessageAlert.warning('아이디 중복검사를 진행해주세요.');
          return false;
        }
        if (isChecked === 'FAIL') {
          MessageAlert.warning('사용할 수 없는 아이디입니다.');
          return false;
        }
        if (!admin_account) {
          MessageAlert.warning('관리자 ID를 올바르게 입력해주세요.');
          return false;
        }
        if (!password || password.length < 8) {
          MessageAlert.warning('비밀번호는 8자리 이상이여야 합니다.');
          return false;
        }
      }

      return {
        ...modalData?.admin,
        shop_information_id: type === 'ADMIN' ? 1 : shop_information_id,
      };
    }
  };

  /**
   * 아이디 중복검사
   * --
   */
  const handleDuplication = async () => {
    const { admin_account } = modalData?.admin;
    if (isChecked === 'SUCCESS') {
      MessageAlert.warning('중복검사를 완료하셨습니다.');
      return false;
    }
    // 관리자 아이디 입력여부 체크
    if (!admin_account) {
      MessageAlert.warning('관리자 아이디를 입력해주세요.');
      return false;
    }

    try {
      // 아이디 중복검사
      const { data, status } = await duplicate({
        admin_account: admin_account,
        type: 'ADMIN',
      });

      if (status !== 200) {
        MessageAlert.warning(
          '아이디 중복검사를 진행하는데 실패하였습니다. 관리자에게 문의해주세요.'
        );
        return;
      }

      if (data) {
        MessageAlert.success('사용가능한 아이디입니다.');
        setIsChecked('SUCCESS');
      } else {
        MessageAlert.warning('사용할 수 없는 아이디입니다.');
        setIsChecked('FAIL');
      }
    } catch (err) {
      console.error(`[ACCOUNT CHECK] ERROR : `, err);
      MessageAlert.error(
        '아이디 중복검사를 진행하는데 실패하였습니다. 관리자에게 문의해주세요.'
      );
    }
  };

  /* ===== HOOKS ===== */
  useEffect(() => {
    const newData = Admins?.data?.map((admin) => ({
      ...admin,
      self: admin.admin_id === userInfo.admin_id ? 'Y' : 'N',
      shop_information_name:
        ShopInformations?.data?.filter(
          (item) => item.shop_information_id === admin.shop_information_id
        )?.[0]?.shop_information_name || '',
    }));

    const newShopData = ShopInformations?.data?.filter(
      (item) => item.application_status === 'APPROVE'
    );
    setShopdata(newShopData);
    setData(newData);
    //eslint-disable-next-line
  }, [Admins, ShopInformations]);

  // console.log('data', data);
  // console.log('modalData', modalData);
  // console.log('newShopData', shopData);
  /* ===== RENDER ===== */
  return (
    <>
      {isLoading && (
        <LoadingPage text={'데이터를 처리중입니다. 잠시만 기다려주세요.'} />
      )}
      <ManagerListPresenter
        data={data}
        shopData={shopData}
        auth={userInfo?.admin_privilege}
        isOpened={isOpened}
        modalType={modalType}
        modalData={modalData}
        onOpen={handleModal}
        onSubmit={handleSubmit}
        onDelete={handleDeleteSelected}
        onChange={handleChange}
        isEditingAccount={isEditingAccount}
        setIsEditingAccount={setIsEditingAccount}
        isChecked={isChecked}
        onDuplication={handleDuplication}
        defaultShop={defaultShop}
      />
    </>
  );
};

export default ManagerListContainer;
